/* @flow */

import React, { useEffect, useMemo, useCallback } from 'react';
import gql from 'graphql-tag';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useSubscription } from '@apollo/react-hooks';
import { useAppState } from 'react-native-hooks';
import { Platform } from 'react-native';

import { tokenSelector } from '../auth/selectors';
import { isConnectedSelector } from '../system/selectors';


const PostponeSettingsFragment = gql`fragment PostponeSettings on MeType {
  startMorningsAtMinuteOffset
  laterTodayDelayMinutes
  startEveningsAtMinuteOffset
}`;

const Query = gql`
query PostponeSettings($token:ID!){
  me(token:$token){
    ...PostponeSettings
  }
}
${PostponeSettingsFragment}
`;

const Subscription = gql`
subscription UpdatePostponeSettings($token: ID!) {
  updatedUser(token: $token){
    ...PostponeSettings
  }
}
${PostponeSettingsFragment}
`;

export const updateSetting = (settings: Object) => (dispatch: Dispatch) => {
  const timeOfChange = new Date();

  Object.keys(settings).forEach((settingName) => {
    dispatch({
      type: 'UPDATE_SETTING',
      payload: {
        [settingName]: settings[settingName],
      },
      meta: {
        offline: {
          effect: {
            type: 'MUTATION',
            mutationName: settingName,
            options: {
              variables: {
                timeOfChange,
                [settingName]: settings[settingName],
              },
            },
          },
          commit: {
            type: 'SETTING_SYNCED',
            meta: { settingName },
          },
          rollback: {
            type: 'SETTING_DISCARDED',
            meta: { /* id, changeId */ },
          },
        },
      },

    });
  });
};

export const useUpdateSetting = () => {
  const dispatch = useDispatch();

  return (settings: Object) => {
    dispatch(updateSetting(settings));
  };
};

const useFetchSettings = (token: string) => {
  const dispatch = useDispatch(),
        {
          data, error,
        } = useQuery(Query, { variables: { token }, pollInterval: Platform.OS === 'android' ? 600000 : 0 });

  useEffect(() => {
    if (!error && data && data.me) {
      dispatch({ type: 'SETTINGS_FETCHED', payload: data.me });
    }
  }, [data, dispatch, error]);

  return () => {
    dispatch({ type: 'SETTINGS_RESET' });
  };
};

const useSubscribeToSettings = (token: string) => {
  const dispatch = useDispatch();

  const onSubscriptionData = useCallback(({ subscriptionData }: Object) => {
    const { updatedUser } = subscriptionData.data;
    if (updatedUser) {
      dispatch({ type: 'SETTINGS_FETCHED', payload: updatedUser });
    }
  }, []); //eslint-disable-line

  useSubscription(Subscription, {
    variables: { token },
    onSubscriptionData,
    shouldResubscribe: true,
  });
};

const InnerFetchPostponeSettings = ({ children, token }: Object) => {
  useFetchSettings(token);

  return <>{ children }</>;
};


const InnerSubscribeToPostponeSettings = ({ children, token }: Object) => {
  useSubscribeToSettings(token);

  return <>{ children }</>;
};

const EmptyComponent = ({ children }: Object) => <>{ children }</>;

export const SubscribeToPostponeSettings = ({ children }: Object) => {
  const token = useSelector(tokenSelector),
        appState = useAppState(),
        isConnected = useSelector(isConnectedSelector),
        SubscriptionComponent = Platform.OS === 'android' ? EmptyComponent : InnerSubscribeToPostponeSettings,
        isNotBackground = appState !== 'background';

  return useMemo(() => token && isNotBackground && isConnected
    ? (
      <SubscriptionComponent token={token}>
        <InnerFetchPostponeSettings
          token={token}
        >
          {
            children
          }
        </InnerFetchPostponeSettings>
      </SubscriptionComponent>
    )
    : <EmptyComponent>{ children }</EmptyComponent>, [
    children,
    isConnected,
    isNotBackground,
    token,
  ]);
};

export default { useFetchSettings };
